import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { normalizeKindredScreenName } from "../../shared/system";

type ProductAnalyticsEventInput = {
  userId?: string | null;
  eventName: string;
  screenName?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  metadata?: Record<string, unknown>;
};

const SESSION_STORAGE_KEY = "kindred_product_analytics_session_id";
const RECENT_EVENT_WINDOW_MS = 4_000;
const SIGNED_URL_EXPIRY_MS = 55 * 60 * 1000;
const recentEventMap = new Map<string, number>();
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

function pruneRecentEvents() {
  const now = Date.now();
  for (const [key, seenAt] of recentEventMap.entries()) {
    if (now - seenAt > RECENT_EVENT_WINDOW_MS) {
      recentEventMap.delete(key);
    }
  }
}

function createSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `kindred-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getSessionId() {
  if (typeof window === "undefined") return createSessionId();

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;

  const next = createSessionId();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, next);
  return next;
}

function buildFingerprint(input: ProductAnalyticsEventInput) {
  return [
    input.userId || "",
    input.eventName,
    input.screenName || "",
    input.entityType || "",
    input.entityId || "",
    JSON.stringify(input.metadata ?? {}),
  ].join("::");
}

export async function trackKindredProductEvent(input: ProductAnalyticsEventInput) {
  if (!input.userId || !input.eventName.trim()) return;

  pruneRecentEvents();
  const fingerprint = buildFingerprint(input);
  if (recentEventMap.has(fingerprint)) return;
  recentEventMap.set(fingerprint, Date.now());

  try {
    await supabase.from("product_analytics_events" as any).insert({
      user_id: input.userId,
      session_id: getSessionId(),
      app_name: "kindred",
      surface: "web",
      event_name: input.eventName.trim(),
      screen_name: input.screenName?.trim() || null,
      entity_type: input.entityType?.trim() || null,
      entity_id: input.entityId?.trim() || null,
      metadata: input.metadata ?? {},
    });
  } catch (error) {
    console.error("Kindred analytics tracking failed", error);
  }
}

export async function trackKindredScreenView(userId: string | null | undefined, pathname: string) {
  if (!userId) return;

  await trackKindredProductEvent({
    userId,
    eventName: "screen_view",
    screenName: normalizeKindredScreenName(pathname),
    metadata: { pathname },
  });
}

function getCacheKey(bucket: string, value: string) {
  return `${bucket}::${value}`;
}

export function readCachedSignedStorageUrl(bucket: string, value: string) {
  const cached = signedUrlCache.get(getCacheKey(bucket, value));
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    signedUrlCache.delete(getCacheKey(bucket, value));
    return null;
  }
  return cached.url;
}

export function writeCachedSignedStorageUrl(bucket: string, value: string, url: string) {
  signedUrlCache.set(getCacheKey(bucket, value), {
    url,
    expiresAt: Date.now() + SIGNED_URL_EXPIRY_MS,
  });
}

export function useCachedValue<T>(value: T) {
  const [state, setState] = useState(value);

  useEffect(() => {
    setState(value);
  }, [value]);

  return state;
}

